import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { openRequestTrackingPage } from "../../../../helper/common/openRequestTrackingPage";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { campaignListActions } from "../../../../redux/slices/campaignList.js";
import CommonFileUploader from "../../../SetupConnectors/subConnectorComponents/CommonFileUploader";
import { FilePreviewCard } from "../../../SetupConnectors/subConnectorComponents/PreviewUploading";
import ValidationStatus from "../../../SetupConnectors/subConnectorComponents/ValidationStatus";

const BULK_ENABLE_PAUSE_TEMPLATE_URL =
  "https://storage.googleapis.com/adyogi_marketplace/Bulk%20Enable%20Pause%20Template.csv";
const JOB_TYPE = "AMAZON_BULK_ASIN_STATUS";
const SOURCE = "amazon";

const ACTION = {
  SKIP: "SKIP",
  BACK_SYNC: "BACK_SYNC",
};

const STEP = {
  IDLE: "IDLE",
  UPLOADING: "UPLOADING",
  CSV_VALIDATING: "CSV_VALIDATING",
  CSV_ERROR: "CSV_ERROR",
  DATA_VALIDATING: "DATA_VALIDATING",
  READY: "READY",
  SUBMITTING: "SUBMITTING",
  SUCCESS: "SUCCESS",
};

function getClientId() {
  try {
    return JSON.parse(localStorage.getItem("clientId"));
  } catch {
    return localStorage.getItem("clientId");
  }
}

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("accountId"));
  } catch {
    return localStorage.getItem("accountId");
  }
}

function getUserEmail() {
  return localStorage.getItem("email") ?? "";
}

async function uploadFileToGcs(file, onProgress) {
  const res = await fetch(
    `${process.env.REACT_APP_LOGIN_API}/api/gcp/preSignedUrl`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `${process.env.REACT_APP_AUTHORIZATION_FOR_NORMAL_USER}`,
      },
      body: JSON.stringify({
        filename: file.name,
        contentType: file.type || "text/csv",
      }),
    }
  );

  const data = await res.json();
  if (!res.ok || !data?.uploadUrl) {
    throw new Error(data?.error || "Failed to get upload URL");
  }

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", data.uploadUrl, true);
    xhr.setRequestHeader("Content-Type", file.type || "text/csv");

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && typeof onProgress === "function") {
        onProgress(Math.round((event.loaded * 100) / event.total));
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 201) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new Error("Upload to GCS failed"));
      }
    };
    xhr.onerror = () => reject(new Error("Upload failed due to network error"));
    xhr.send(file);
  });

  if (!data.publicUrl) {
    throw new Error("No public URL returned");
  }

  return data.publicUrl;
}

async function validateCsvApi(clientId, blobUrl) {
  const url = `${process.env.REACT_APP_CATALOG_API}/client/${clientId}:validate-csv?showWarnings=true&source=${SOURCE}`;
  const { data } = await axios.post(url, {
    blobUrl,
    jobType: JOB_TYPE,
    stopAtFirstError: false,
  });
  return data;
}

async function updateBulkAsinsStatusApi({
  clientId,
  status,
  email,
  fileUrl,
  actionType,
}) {
  const url = `${process.env.REACT_APP_ADS_API}/client/${clientId}:update-bulk-asins-status?source=${SOURCE}&skipEvents=true`;
  const { data } = await axios.post(url, {
    status,
    email,
    file_url: fileUrl,
    action_type: actionType,
  });
  return data;
}

async function partialBackSyncApi(clientId, accountId) {
  const url = `${process.env.REACT_APP_ADS_API}/client/${clientId}:partialBackSyncAdEntities?account_id=${accountId}&source=${SOURCE}&tillLevel=ADSET&mode=ASYNC&email=${getUserEmail()}`;
  const { data } = await axios.post(url);
  return data;
}

function ConfirmActionDialog({
  open,
  title,
  description,
  confirmLabel,
  loading,
  onCancel,
  onConfirm,
}) {
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 16px 48px rgba(49, 46, 129, 0.18)",
        },
      }}
    >
      <Box
        sx={{
          background: "linear-gradient(135deg, #5B4BDB 0%, #7C6FF0 100%)",
          px: 3,
          py: 2.5,
          color: "#fff",
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          Please confirm before continuing
        </Typography>
      </Box>
      <DialogContent sx={{ pt: 3, pb: 1 }}>
        <Typography variant="body1" color="text.secondary">
          {description}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mt={1.5}
        >
          *SB ads not supported at the moment
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
          variant="outlined"
          sx={{ borderRadius: 2, textTransform: "none", minWidth: 96 }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          sx={{
            borderRadius: 2,
            textTransform: "none",
            minWidth: 110,
            background: "linear-gradient(135deg, #5B4BDB 0%, #7C6FF0 100%)",
            boxShadow: "0 6px 16px rgba(91, 75, 219, 0.35)",
          }}
        >
          {loading ? "Please wait..." : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CSVUploadModal(props) {
  const dispatch = useDispatch();
  const csvUploadModalInGlobalAd = useSelector(
    (state) => state.campaignListData.csvUploadModalInGlobalAd
  );

  const [selectedOption, setSelectedOption] = useState("Enable");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [fileUrl, setFileUrl] = useState("");
  const [step, setStep] = useState(STEP.IDLE);
  const [csvValidation, setCsvValidation] = useState({});
  const [dataValidation, setDataValidation] = useState(null);
  const [resolutionAction, setResolutionAction] = useState(ACTION.SKIP);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successMeta, setSuccessMeta] = useState({ type: null, message: "" });

  const unmatchedCampaigns = dataValidation?.unmatched_campaign_names || [];
  const unresolvedAsins = dataValidation?.unresolved_asins || [];
  const dataErrors = dataValidation?.errors || [];
  const hasUnmatched = unmatchedCampaigns.length > 0;
  const hasUnresolvedAsins = unresolvedAsins.length > 0;
  const hasDataErrors = dataErrors.length > 0;
  const hasDataWarnings = hasUnmatched || hasUnresolvedAsins;
  const isBusy =
    step === STEP.UPLOADING ||
    step === STEP.CSV_VALIDATING ||
    step === STEP.DATA_VALIDATING ||
    step === STEP.SUBMITTING;
  const canSubmit = step === STEP.READY && !!fileUrl && !hasDataErrors;
  const isSuccess = step === STEP.SUCCESS;

  const resetAll = () => {
    setSelectedOption("Enable");
    setFile(null);
    setProgress(0);
    setFileUrl("");
    setStep(STEP.IDLE);
    setCsvValidation({});
    setDataValidation(null);
    setResolutionAction(ACTION.SKIP);
    setConfirmOpen(false);
    setSuccessMeta({ type: null, message: "" });
  };

  const resetFileFlow = () => {
    setFile(null);
    setProgress(0);
    setFileUrl("");
    setStep(STEP.IDLE);
    setCsvValidation({});
    setDataValidation(null);
    setResolutionAction(ACTION.SKIP);
    setSuccessMeta({ type: null, message: "" });
  };

  useEffect(() => {
    if (!csvUploadModalInGlobalAd) {
      resetAll();
    }
  }, [csvUploadModalInGlobalAd]);

  const handleClose = () => {
    if (isBusy) return;
    dispatch(campaignListActions.hideCsvUploadInGlobalAd());
  };

  const downloadTemplate = () => {
    const link = document.createElement("a");
    link.href = BULK_ENABLE_PAUSE_TEMPLATE_URL;
    link.setAttribute("download", "Bulk Enable Pause Template.csv");
    link.setAttribute("target", "_blank");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openRequestTracking = () => {
    openRequestTrackingPage();
  };

  const runDataValidation = async (publicUrl) => {
    const clientId = getClientId();
    setStep(STEP.DATA_VALIDATING);
    try {
      const data = await updateBulkAsinsStatusApi({
        clientId,
        status: selectedOption === "Enable" ? "enabled" : "paused",
        email: getUserEmail(),
        fileUrl: publicUrl,
        actionType: "VALIDATE_DATA",
      });
      setDataValidation(data || {});
      setResolutionAction(ACTION.SKIP);
      setStep(STEP.READY);
    } catch (err) {
      console.error(err);
      toast.error("Unable to validate data. Please try again.");
      setStep(STEP.CSV_ERROR);
      setCsvValidation({
        status: "error",
        warningAndErrorsData: [
          {
            lineNumber: "-",
            messages: ["Data validation failed. Please re-upload the file."],
          },
        ],
      });
    }
  };

  const runCsvValidation = async (publicUrl) => {
    const clientId = getClientId();
    setStep(STEP.CSV_VALIDATING);
    try {
      const data = await validateCsvApi(clientId, publicUrl);
      const errors = data?.errors || [];

      if (errors.length > 0) {
        setCsvValidation({
          status: "error",
          uploadedRows: data?.rowsScanned || 0,
          currentRows: data?.currentRows || 0,
          delta: data?.delta || 0,
          warningAndErrorsData: errors,
        });
        setStep(STEP.CSV_ERROR);
        return;
      }

      setCsvValidation({
        status: "success",
        uploadedRows: data?.rowsScanned || 0,
        currentRows: data?.currentRows || 0,
        delta: data?.delta || 0,
        warningAndErrorsData: [],
      });
      await runDataValidation(publicUrl);
    } catch (err) {
      console.error(err);
      toast.error("Unable to validate CSV. Please try again.");
      setCsvValidation({
        status: "error",
        warningAndErrorsData: [
          {
            lineNumber: "-",
            messages: ["CSV validation failed. Please try again."],
          },
        ],
      });
      setStep(STEP.CSV_ERROR);
    }
  };

  const handleUploadBlob = async (selectedFile) => {
    if (!selectedFile) {
      toast.error("Please select a file first.");
      return;
    }

    setFile(selectedFile);
    setProgress(0);
    setFileUrl("");
    setCsvValidation({});
    setDataValidation(null);
    setResolutionAction(ACTION.SKIP);
    setSuccessMeta({ type: null, message: "" });
    setStep(STEP.UPLOADING);

    try {
      const publicUrl = await uploadFileToGcs(selectedFile, setProgress);
      setFileUrl(publicUrl);
      await runCsvValidation(publicUrl);
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(`Upload failed: ${error.message}`);
      resetFileFlow();
    }
  };

  const handleSubmitClick = () => {
    if (!canSubmit) return;
    setConfirmOpen(true);
  };

  const executeConfirmedAction = async () => {
    if (hasDataErrors) return;
    const clientId = getClientId();
    const accountId = getAccountId();
    const useBackSync = hasUnmatched && resolutionAction === ACTION.BACK_SYNC;

    setStep(STEP.SUBMITTING);
    try {
      if (useBackSync) {
        if (!accountId) {
          throw new Error("Account ID not found");
        }
        await partialBackSyncApi(clientId, accountId);
        setSuccessMeta({
          type: "backsync",
          message:
            "Your back sync request has been submitted. You can track it on the Request Tracking page.",
        });
      } else {
        await updateBulkAsinsStatusApi({
          clientId,
          status: selectedOption === "Enable" ? "enabled" : "paused",
          email: getUserEmail(),
          fileUrl,
          actionType: "SUBMIT",
        });
        setSuccessMeta({
          type: "submit",
          message:
            "Your bulk action request has been submitted successfully. You can track it on the Request Tracking page.",
        });
      }
      setConfirmOpen(false);
      setStep(STEP.SUCCESS);
    } catch (err) {
      console.error(err);
      toast.error(
        useBackSync
          ? "Failed to submit back sync request. Please try again."
          : "Failed to submit request. Please try again."
      );
      setStep(STEP.READY);
    }
  };

  const actionCards = [
    {
      id: "Enable",
      title: "Bulk Enable/Add",
      desc: "Enable product ads for ASINs in the uploaded CSV.",
    },
    {
      id: "Pause",
      title: "Bulk Pause",
      desc: "Pause product ads for ASINs in the uploaded CSV.",
    },
  ];

  const confirmDescription =
    hasUnmatched && resolutionAction === ACTION.BACK_SYNC
      ? "Are you sure you want to start a back sync for unmatched campaigns?"
      : `Are you sure you want to ${selectedOption.toLowerCase()} the uploaded ASINs?`;

  const csvStatusForUi =
    step === STEP.CSV_VALIDATING
      ? "validating"
      : step === STEP.CSV_ERROR
        ? "error"
        : "";

  return (
    <>
      <Dialog
        open={!!props.show}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={isBusy}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            background: "linear-gradient(135deg, #F7F6FF 0%, #FFFFFF 100%)",
            borderBottom: "1px solid #ECEEF2",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              Bulk Enable / Pause
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload CSV, validate, and submit bulk ASIN actions
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={isBusy}
            size="small"
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2.5 }}>
          {isSuccess ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
              gap={2}
              py={3}
            >
              <CheckCircleIcon sx={{ fontSize: 56, color: "success.main" }} />
              <Typography variant="h6" fontWeight={700}>
                {successMeta.type === "backsync"
                  ? "Back Sync Submitted"
                  : "Request Submitted"}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 420 }}
              >
                {successMeta.message}
              </Typography>
              <Button
                variant="contained"
                endIcon={<OpenInNewIcon />}
                onClick={openRequestTracking}
                sx={{
                  mt: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  px: 2.5,
                  background:
                    "linear-gradient(135deg, #5B4BDB 0%, #7C6FF0 100%)",
                }}
              >
                Go to Request Tracking
              </Button>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" py={2} gap={2.5}>
              <Box display="flex" gap={2}>
                {actionCards.map((card) => {
                  const selected = selectedOption === card.id;
                  return (
                    <Box
                      key={card.id}
                      onClick={() => {
                        if (isBusy || file) return;
                        setSelectedOption(card.id);
                      }}
                      sx={{
                        flex: 1,
                        position: "relative",
                        py: 1.5,
                        px: 2,
                        pr: 5,
                        border: "1px solid",
                        borderColor: selected ? "primary.main" : "#E1E3E5",
                        bgcolor: selected ? "#F7F6FF" : "#fff",
                        borderRadius: "8px",
                        cursor: isBusy || file ? "default" : "pointer",
                        opacity: isBusy || file ? 0.85 : 1,
                        transition: "0.2s",
                      }}
                    >
                      <Checkbox
                        checked={selected}
                        tabIndex={-1}
                        disableRipple
                        icon={
                          <RadioButtonUncheckedIcon
                            sx={{ fontSize: 22, color: "#E1E3E5" }}
                          />
                        }
                        checkedIcon={
                          <CheckCircleIcon
                            sx={{ fontSize: 22, color: "primary.main" }}
                          />
                        }
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          p: 0.5,
                          pointerEvents: "none",
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        gutterBottom
                        sx={{ color: selected ? "primary.main" : "inherit" }}
                      >
                        {card.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {card.desc}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {!file && step !== STEP.CSV_VALIDATING && (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      onClick={downloadTemplate}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        cursor: "pointer",
                        fontWeight: 500,
                        color: "primary.main",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      <DownloadIcon fontSize="small" />
                      Download Template
                    </Typography>
                  </Box>
                  <CommonFileUploader
                    disabled={isBusy}
                    onFileSelect={handleUploadBlob}
                  />
                </Box>
              )}

              {file && (
                <FilePreviewCard
                  key={file.name}
                  fileName={file?.name}
                  fileSize={file?.size}
                  status={progress === 100 ? "Uploaded" : "Uploading..."}
                  showDeleteIcon={!isBusy}
                  onDelete={resetFileFlow}
                  progress={progress}
                />
              )}

              {(step === STEP.CSV_VALIDATING || step === STEP.CSV_ERROR) && (
                <ValidationStatus
                  key={`${csvStatusForUi}-${csvValidation.uploadedRows || 0}`}
                  status={csvStatusForUi}
                  validationStatusData={csvValidation}
                />
              )}

              {step === STEP.DATA_VALIDATING && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#F7F6FF",
                    color: "primary.main",
                  }}
                >
                  <CircularProgress size={20} thickness={4} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Validating data...
                  </Typography>
                </Box>
              )}

              {step === STEP.READY && dataValidation && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: hasDataErrors
                      ? "#FDEDED"
                      : hasDataWarnings
                        ? "#FFF4E5"
                        : "#EDF7ED",
                    border: "1px solid",
                    borderColor: hasDataErrors
                      ? "#F5C2C7"
                      : hasDataWarnings
                        ? "#FFE0B2"
                        : "#C8E6C9",
                  }}
                >
                  <Box display="flex" alignItems="flex-start" gap={1.5}>
                    {hasDataErrors ? (
                      <ErrorIcon sx={{ color: "error.main", mt: 0.25 }} />
                    ) : hasDataWarnings ? (
                      <WarningAmberIcon sx={{ color: "warning.main", mt: 0.25 }} />
                    ) : (
                      <CheckCircleIcon sx={{ color: "success.main", mt: 0.25 }} />
                    )}
                    <Box flex={1}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        color={
                          hasDataErrors
                            ? "#5F2120"
                            : hasDataWarnings
                              ? "#663C00"
                              : "success.main"
                        }
                      >
                        {hasDataErrors
                          ? "Data validation failed"
                          : hasDataWarnings
                            ? "Data validation found issues"
                            : "Data validation successful"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Unique ASINs:{" "}
                        <b>{dataValidation.unique_asin_count ?? 0}</b>
                        {" · "}
                        Unique Campaigns:{" "}
                        <b>{dataValidation.unique_campaign_count ?? 0}</b>
                      </Typography>

                      {hasDataErrors && (
                        <>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            mt={1.5}
                            color="#5F2120"
                          >
                            Errors ({dataErrors.length})
                          </Typography>
                          <List
                            dense
                            sx={{
                              listStyleType: "disc",
                              pl: 2.5,
                              maxHeight: 120,
                              overflow: "auto",
                              bgcolor: "rgba(255,255,255,0.55)",
                              borderRadius: 1,
                              mt: 0.5,
                            }}
                          >
                            {dataErrors.map((errorMsg, index) => (
                              <ListItem
                                key={`data-error-${index}`}
                                sx={{ display: "list-item", p: 0, py: 0.25 }}
                              >
                                <ListItemText
                                  primary={errorMsg}
                                  primaryTypographyProps={{
                                    variant: "body2",
                                    color: "#5F2120",
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      )}

                      {hasUnresolvedAsins && (
                        <>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            mt={1.5}
                            color={hasDataErrors ? "#5F2120" : "#663C00"}
                          >
                            Unresolved ASINs ({unresolvedAsins.length})
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mt={0.25}
                          >
                            These ASINs do not exist or are invalid.
                          </Typography>
                          <List
                            dense
                            sx={{
                              listStyleType: "disc",
                              pl: 2.5,
                              maxHeight: 100,
                              overflow: "auto",
                              bgcolor: "rgba(255,255,255,0.55)",
                              borderRadius: 1,
                              mt: 0.5,
                            }}
                          >
                            {unresolvedAsins.map((asin) => (
                              <ListItem
                                key={asin}
                                sx={{ display: "list-item", p: 0, py: 0.25 }}
                              >
                                <ListItemText
                                  primary={asin}
                                  primaryTypographyProps={{ variant: "body2" }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      )}

                      {hasUnmatched && (
                        <>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            mt={1.5}
                            color={hasDataErrors ? "#5F2120" : "#663C00"}
                          >
                            Unmatched campaign names ({unmatchedCampaigns.length})
                          </Typography>
                          <List
                            dense
                            sx={{
                              listStyleType: "disc",
                              pl: 2.5,
                              maxHeight: 100,
                              overflow: "auto",
                              bgcolor: "rgba(255,255,255,0.55)",
                              borderRadius: 1,
                              mt: 0.5,
                            }}
                          >
                            {unmatchedCampaigns.map((name) => (
                              <ListItem
                                key={name}
                                sx={{ display: "list-item", p: 0, py: 0.25 }}
                              >
                                <ListItemText
                                  primary={name}
                                  primaryTypographyProps={{ variant: "body2" }}
                                />
                              </ListItem>
                            ))}
                          </List>

                          {!hasDataErrors && (
                            <>
                              <Divider sx={{ my: 1.5 }} />

                              <Typography
                                variant="body2"
                                fontWeight={600}
                                mb={1}
                                color="#663C00"
                              >
                                How do you want to proceed?
                              </Typography>
                              <RadioGroup
                                value={resolutionAction}
                                onChange={(e) =>
                                  setResolutionAction(e.target.value)
                                }
                              >
                                <FormControlLabel
                                  value={ACTION.SKIP}
                                  control={<Radio size="small" />}
                                  label={
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        fontWeight={600}
                                      >
                                        Skip
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Continue with submit and skip unmatched
                                        campaigns
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{ alignItems: "flex-start", mb: 0.5 }}
                                />
                                <FormControlLabel
                                  value={ACTION.BACK_SYNC}
                                  control={<Radio size="small" />}
                                  label={
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        fontWeight={600}
                                      >
                                        Back Sync
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Trigger back sync instead of submitting
                                        this file
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{ alignItems: "flex-start" }}
                                />
                              </RadioGroup>
                            </>
                          )}
                        </>
                      )}

                      {hasDataErrors && (
                        <Typography
                          mt={1.5}
                          variant="body2"
                          color="#5F2120"
                          fontWeight={500}
                        >
                          Please fix the errors and re-upload the file to continue.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        {!isSuccess && (
          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: "1px solid #ECEEF2",
              gap: 1,
            }}
          >
            <Button
              onClick={handleClose}
              disabled={isBusy}
              variant="outlined"
              sx={{ borderRadius: 2, textTransform: "none", minWidth: 100 }}
            >
              Cancel
            </Button>
            <Button
              id={props?.confirmationId}
              onClick={handleSubmitClick}
              disabled={!canSubmit || isBusy}
              variant="contained"
              sx={{
                borderRadius: 2,
                textTransform: "none",
                minWidth: 110,
                background: "linear-gradient(135deg, #5B4BDB 0%, #7C6FF0 100%)",
                "&.Mui-disabled": {
                  cursor: "not-allowed",
                  pointerEvents: "auto",
                },
              }}
            >
              {step === STEP.SUBMITTING ? "Submitting..." : "Submit"}
            </Button>
          </DialogActions>
        )}

        {isSuccess && (
          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: "1px solid #ECEEF2",
            }}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ borderRadius: 2, textTransform: "none", minWidth: 100 }}
            >
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <ConfirmActionDialog
        open={confirmOpen}
        title="Confirm Bulk Action"
        description={confirmDescription}
        confirmLabel={
          hasUnmatched && resolutionAction === ACTION.BACK_SYNC
            ? "Back Sync"
            : "Confirm"
        }
        loading={step === STEP.SUBMITTING}
        onCancel={() => {
          if (step === STEP.SUBMITTING) return;
          setConfirmOpen(false);
        }}
        onConfirm={executeConfirmedAction}
      />
    </>
  );
}
